import { useState, useEffect } from "react";
import { Link, useLocation } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import logo from "@/assets/logo.png";
import { conversions } from "@/lib/analytics";
import { PHONE_DISPLAY, PHONE_TEL_HREF } from "@/config/business";
import {
  ESTIMATOR_HREF,
  PRIMARY_CTA_LABEL,
  SECONDARY_CTA_LABEL,
  scrollToEstimator,
} from "@/lib/primaryCta";

const services = [
  { label: "Dog Fence Installation", to: "/services/dog-fence" },
  { label: "Deer Fence Installation", to: "/services/deer-fence" },
  { label: "Garden Fence Installation", to: "/services/garden-fence" },
  { label: "Fence Restoration", to: "/services/fence-restoration" },
  { label: "Fence Tune-Up ($299)", to: "/fence-tuneup", badge: "New" },
  { label: "AI Restoration Visualizer", to: "/restoration-preview" },
];

const company = [
  { label: "About", to: "/about" },
  { label: "Learn", to: "/learn" },
  { label: "Refer & Earn $100", to: "/refer", badge: "New" },
  {
    label: "Franchise Opportunities",
    to: "/franchise-opportunities",
    badge: "New",
  },
  { label: "Contact", to: "/contact" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
    setCompanyOpen(false);
  }, [location]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      /* Positioning is owned by SiteHeader, which fixes the navbar + trust bar
         together and reserves their real combined height in flow. */
      className={`relative w-full transition-all duration-300 ${
        scrolled
          ? "bg-background shadow-md border-b border-border"
          : "bg-background/90 backdrop-blur-xs border-b border-transparent"
      }`}
    >

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 sm:h-20 lg:h-28">
        <Link to="/" className="flex items-center">
          <img
            src={logo}
            alt="Modern Fence Install"
            className="w-auto h-11 sm:h-14 lg:h-20"
            width={350}
            height={116}
            fetchPriority="high"
            decoding="async"
          />
        </Link>
        <div className="hidden lg:flex items-center gap-5">
          {/* Services dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button className="flex items-center gap-1 text-sm text-foreground/70 hover:text-foreground transition-colors py-4">
              Services <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {servicesOpen && (
              <div className="absolute top-full left-0 bg-background border border-border rounded-lg shadow-xl py-2 min-w-[220px]">
                {services.map((s) => (
                  <Link
                    key={s.to}
                    to={s.to}
                    className="flex items-center justify-between gap-2 px-4 py-2 text-sm text-foreground/70 hover:text-foreground hover:bg-muted transition-colors"
                  >
                    <span>{s.label}</span>
                    {(s as any).badge && (
                      <span className="text-[10px] font-bold uppercase tracking-wide bg-primary text-primary-foreground px-1.5 py-0.5 rounded-sm">
                        {(s as any).badge}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <Link
            to="/locations"
            className="text-sm text-foreground/70 hover:text-foreground transition-colors"
          >
            Service Areas
          </Link>
          <Link
            to="/gallery"
            className="text-sm text-foreground/70 hover:text-foreground transition-colors"
          >
            Gallery
          </Link>
          {/* Company dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setCompanyOpen(true)}
            onMouseLeave={() => setCompanyOpen(false)}
          >
            <button className="flex items-center gap-1 text-sm text-foreground/70 hover:text-foreground transition-colors py-4">
              Company <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {companyOpen && (
              <div className="absolute top-full left-0 bg-background border border-border rounded-lg shadow-xl py-2 min-w-[220px]">
                {company.map((c) => (
                  <Link
                    key={c.to}
                    to={c.to}
                    className="flex items-center justify-between gap-2 px-4 py-2 text-sm text-foreground/70 hover:text-foreground hover:bg-muted transition-colors"
                  >
                    <span>{c.label}</span>
                    {(c as any).badge && (
                      <span className="text-[10px] font-bold uppercase tracking-wide bg-primary text-primary-foreground px-1.5 py-0.5 rounded-sm">
                        {(c as any).badge}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <a
            href={PHONE_TEL_HREF}
            onClick={() => conversions.phoneClick("navbar_desktop")}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground hover:text-primary transition-colors"
          >
            <Phone className="w-4 h-4 text-primary" />
            {PHONE_DISPLAY}
          </a>
          {/* Secondary — lower emphasis than the price CTA */}
          <Link
            to="/book"
            className="text-sm font-medium text-foreground/70 hover:text-primary transition-colors"
          >
            Book Consultation
          </Link>
          {/* PRIMARY — single dominant navbar CTA */}
          <Button variant="cta" size="sm" className="px-4" asChild>
            <a href={ESTIMATOR_HREF} onClick={(e) => scrollToEstimator(e)}>
              {PRIMARY_CTA_LABEL}
            </a>
          </Button>
          <Button variant="outline" size="sm" className="px-4" asChild>
            <Link to="/order">Reserve Your Fence Install</Link>
          </Button>
          <Link
            to="/account"
            className="text-sm text-foreground/70 hover:text-primary transition-colors font-medium"
          >
            Account
          </Link>
        </div>

        {/* Mobile tap-to-call + menu toggle */}
        <div className="flex items-center gap-2 lg:hidden">
          <a
            href="tel:+18884163144"
            onClick={() => conversions.phoneClick("navbar_mobile_header")}
            aria-label="Call Modern Fence Install"
            className="inline-flex flex-col items-end leading-tight px-2 py-1.5 rounded-md text-primary hover:bg-primary/5 active:bg-primary/10 transition-colors min-h-[44px] justify-center"
          >
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold">
              <Phone className="w-4 h-4" />
              Call
            </span>
            <span className="text-[10px] font-medium text-muted-foreground">
              Speak with our team
            </span>
          </a>
          <button
            className="text-foreground p-2 -mr-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {open && (
        <div
          /* Absolutely positioned so opening the menu never changes the fixed
             header's measured height (which would push page content down). */
          className="lg:hidden absolute top-full left-0 right-0 z-50 shadow-xl bg-background border-t border-border px-4 pb-4 space-y-1 max-h-[calc(100dvh-4rem)] sm:max-h-[calc(100dvh-5rem)] overflow-y-auto overscroll-contain"
          onClick={() => setOpen(false)}
        >
          {/* Primary CTAs first — high-intent visitors shouldn't have to scroll the menu */}
          <div className="flex flex-col gap-2 pt-3 pb-3 border-b border-border mb-2">
            <Button variant="cta" size="sm" className="w-full h-12" asChild>
              <a
                href={ESTIMATOR_HREF}
                onClick={(e) => {
                  setOpen(false);
                  scrollToEstimator(e);
                }}
              >
                {PRIMARY_CTA_LABEL}
              </a>
            </Button>
            <Button variant="outline" size="sm" className="w-full h-12" asChild>
              <Link to="/book">{SECONDARY_CTA_LABEL}</Link>
            </Button>
            <a
              href={PHONE_TEL_HREF}
              onClick={() => conversions.phoneClick("navbar_mobile_menu")}
              className="flex flex-col items-center justify-center gap-0.5 text-sm text-primary font-semibold min-h-[48px] py-1 rounded-md border border-primary/30"
            >
              <span className="inline-flex items-center gap-2">
                <Phone className="w-4 h-4" />
                {PHONE_DISPLAY}
              </span>
              <span className="text-[11px] font-medium text-muted-foreground">
                Speak with our team
              </span>
            </a>
            {/* Supporting high-intent options — deliberately below the primary actions */}
            <div className="flex flex-col gap-1 pt-1">
              <Link
                to="/order"
                className="flex items-center justify-center text-xs font-semibold text-foreground/70 hover:text-primary min-h-[44px]"
              >
                Reserve your fence install · 50% deposit →
              </Link>
              <Link
                to="/beat-my-estimate"
                className="flex items-center justify-center text-xs font-semibold text-foreground/70 hover:text-primary min-h-[44px]"
              >
                Have a competitor quote? We'll beat it →
              </Link>
            </div>
          </div>
          <p className="text-xs text-muted-foreground uppercase tracking-widest pt-2 pb-1 px-2">
            Services
          </p>
          {services.map((s) => (
            <Link
              key={s.to}
              to={s.to}
              className="flex items-center gap-2 text-sm text-foreground/70 hover:text-foreground py-2 pl-4"
            >
              <span>{s.label}</span>
              {(s as any).badge && (
                <span className="text-[10px] font-bold uppercase tracking-wide bg-primary text-primary-foreground px-1.5 py-0.5 rounded-sm">
                  {(s as any).badge}
                </span>
              )}
            </Link>
          ))}
          <Link
            to="/locations"
            className="block text-sm text-foreground/70 hover:text-foreground py-2"
          >
            Service Areas
          </Link>
          <Link
            to="/gallery"
            className="block text-sm text-foreground/70 hover:text-foreground py-2"
          >
            Gallery
          </Link>
          <Link
            to="/learn"
            className="block text-sm text-foreground/70 hover:text-foreground py-2"
          >
            Learn
          </Link>
          <Link
            to="/about"
            className="block text-sm text-foreground/70 hover:text-foreground py-2"
          >
            About
          </Link>
          <Link
            to="/contact"
            className="block text-sm text-foreground/70 hover:text-foreground py-2"
          >
            Contact
          </Link>
          <Link
            to="/refer"
            className="block text-sm text-foreground/70 hover:text-foreground py-2"
          >
            Refer & Earn $100
          </Link>
          <Link
            to="/franchise-opportunities"
            className="block text-sm text-foreground/70 hover:text-foreground py-2"
          >
            Franchise Opportunities
          </Link>
          <Link
            to="/account"
            className="block text-sm text-foreground/70 hover:text-foreground py-2 font-medium"
          >
            My Account
          </Link>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
